import { getFromDataPath, mutateByPath, reduceDataPath, removeByPath } from "../../utility";
import { ARSStateUpdate } from "../contexts";
import { isGroupingFactor, isOperation } from "../typeGuards";
import {
  arst_groupingFactorToTableGroup,
  arst_operationToTableGroup,
  arst_tableGroupDepth,
  blankRowHeaderCell,
  buildArsTableScaffoldModel,
} from "./functions";
import { getColumnCells } from "./functions/getColumnCells";
import {
  ARST_ADD_OPERATION_TO_GROUP,
  ARST_CHANGE_SENT,
  ARST_LOAD_DATA,
  ARST_PATCH_DATA,
  ARST_REMOVE_DATA,
  ARST_REMOVE_GROUPINGFACTOR,
  ARST_SET_FIRST_COLUMN,
  ARST_SET_FIRST_ROW,
  ARST_SET_SCAFFOLD_COL_WIDTH,
  ARST_SET_SCAFFOLD_ROW_HEIGHT,
  ARST_SET_SIDEBAR_FILTER,
  ARST_SET_SIDEBAR_VIEW,
  ARST_SET_TLF_VIEW_MODE,
  ARST_TOGGLE_GROUP_LABEL_VISIBILITY,
  ARST_TOGGLE_SIDEBAR,
  ARST_UPDATE_SCAFFOLD,
  ArsTableAction,
  ArsTableBody,
  ArsTableCellData,
  ArsTableCellType,
  ArsTableGroup,
  ArsTableRowHeader,
  ArsTableState,
  defaultRowHeight,
} from "./interface";
import {
  isArsTableColumnGroupLabel,
  isArsTableColumnOperationCell,
  isArsTableRowHeaderCell,
} from "./typeguards";
import { isArsTableSubGroup } from "./typeguards/isArsTableSubGroup";

/**
 * Reducer function to manage state updates for the ArsTable component.
 *
 * @param state - Current state of the ArsTable
 * @param action - Action to be performed on the state
 * @returns Updated state
 */
export const arsTableReducer = <T>(
  state: ArsTableState<T>,
  action: ArsTableAction<T>,
): ArsTableState<T> => {
  switch (action.operation) {
    case ARST_ADD_OPERATION_TO_GROUP: {
      const targetCell: ArsTableCellData<T> | undefined =
        state.scaffoldModel?.cellData[action.rowId]?.[action.columnId];
      if (targetCell) {
        const targetDataPath = targetCell.dataPath;
        const targetGroup = getFromDataPath(targetDataPath, state.bodyData, (obj) =>
          isArsTableSubGroup(obj, state.settings.editorProps.isT),
        );
        if (targetGroup) {
          const currentGroups = targetGroup.subGroups ?? [];
          const addGroups = action.payload
            .map((item) => item.dropItem)
            .filter((i) => isOperation(i))
            .map((op) => arst_operationToTableGroup(op, state.settings.editorProps));
          const newGroups = [...currentGroups, ...addGroups];
          const newBodyData = mutateByPath(
            state.bodyData,
            `${targetDataPath}/subGroups`,
            newGroups,
          );
          return {
            ...state,
            bodyData: newBodyData,
            changed: true,
          };
        }
      }
      return state;
    }
    case ARST_CHANGE_SENT:
      return {
        ...state,
        changed: false,
      };
    case ARST_LOAD_DATA:
      return {
        ...state,
        bodyData: action.bodyData,
        changed: false,
      };
    case ARST_PATCH_DATA: {
      let newBodyData = { ...state.bodyData };
      let scaffoldModel = state.scaffoldModel;
      const updates: ARSStateUpdate[] =
        "updates" in action ? action.updates : [{ dataPath: action.dataPath, value: action.value }];
      for (const update of updates) {
        if (update.dataPath.startsWith("scaffoldModel")) {
          if (!scaffoldModel) continue;
          scaffoldModel = mutateByPath(
            scaffoldModel,
            update.dataPath.replace("scaffoldModel/", ""),
            update.value,
          );
        } else {
          newBodyData = mutateByPath(newBodyData, update.dataPath, update.value);
        }
      }
      const newState = {
        ...state,
        bodyData: newBodyData,
        scaffoldModel: scaffoldModel,
        changed: true,
      };
      return newState;
    }
    case ARST_REMOVE_DATA: {
      const newBodyData = removeByPath(state.bodyData, action.dataPath, action.newLength);
      const newState = {
        ...state,
        bodyData: newBodyData,
        changed: true,
      };
      return newState;
    }
    case ARST_REMOVE_GROUPINGFACTOR: {
      if (
        action.cellValue.cellType === ArsTableCellType.ColumnGroupCell ||
        action.cellValue.cellType === ArsTableCellType.RowGroupCell
      ) {
        const newBodyData = removeByPath(
          state.bodyData,
          action.cellValue.dataPath,
          action.cellValue.siblingCount - 1,
        );
        if (
          action.cellValue.cellType === ArsTableCellType.RowGroupCell &&
          state.bodyData.rows.length === 1
        ) {
          const pathParts = action.cellValue.dataPath.split("/").filter((p) => p !== "");
          const depth = pathParts.filter((p) => p === "subGroups").length;
          if (depth === 0) {
            // Removing the only root group – no rowHeaders remain
            newBodyData.rowHeaders = [];
          } else {
            // Count rowHeaders contributed by each ancestor level (0..depth-1)
            let headerCount = 0;
            let currentGroup: ArsTableGroup<T> = state.bodyData.rows[0];
            for (let d = 0; d < depth; d++) {
              headerCount += currentGroup.hidden ? 1 : 2;
              if (d < depth - 1) {
                // Path structure: /rows/0[/subGroups/N]... where each subGroups/N pair
                // adds 2 parts. The index of the (d+1)th subGroup is at pathParts[2*(d+1)+1].
                const subGroupIdx = parseInt(pathParts[2 * (d + 1) + 1], 10);
                /* istanbul ignore else */
                if (isArsTableSubGroup(currentGroup, state.settings.editorProps.isT)) {
                  currentGroup = currentGroup.subGroups![subGroupIdx];
                }
              }
            }
            newBodyData.rowHeaders = newBodyData.rowHeaders.slice(0, headerCount);
          }
        }
        const newState = {
          ...state,
          bodyData: newBodyData,
          changed: true,
        };
        return newState;
      }
      break;
    }
    case ARST_SET_FIRST_COLUMN: {
      const newTableGroups = action.payload
        .map((item) => item.dropItem)
        .filter(isGroupingFactor)
        .map((gf) => arst_groupingFactorToTableGroup(gf, state.settings.editorProps));
      const newColumns = [...newTableGroups, ...state.bodyData.columns];
      let newRowHeaders = state.bodyData.rowHeaders;
      const newColumnDepth = Math.max(
        ...newColumns.map((group) => arst_tableGroupDepth(group, state.settings.editorProps)),
        1,
      );
      const currentRowHeights =
        state.bodyData.rowHeaders.length > 0 ? state.bodyData.rowHeaders[0].heights : [];
      if (currentRowHeights.length !== newColumnDepth) {
        const newRowHeights = [
          ...currentRowHeights.slice(0, newColumnDepth),
          ...Array.from(
            { length: newColumnDepth - currentRowHeights.length },
            () => defaultRowHeight,
          ),
        ];
        const rowHeaderDepth = Math.max(
          ...state.bodyData.rows.map((group) =>
            arst_tableGroupDepth(group, state.settings.editorProps),
          ),
          1,
        );
        newRowHeaders = [
          ...newRowHeaders.map((rh) => ({ ...rh, heights: newRowHeights })),
          ...Array.from({ length: rowHeaderDepth - newRowHeaders.length }, () => ({
            ...blankRowHeaderCell(state.settings.editorProps.blankT, newRowHeights),
          })),
        ];
      }
      const newBodyData: ArsTableBody<T> = {
        ...state.bodyData,
        columns: newColumns,
        rowHeaders: newRowHeaders,
      };
      return {
        ...state,
        bodyData: newBodyData,
        changed: true,
      };
    }
    case ARST_SET_FIRST_ROW: {
      const newTableGroups = action.payload
        .map((item) => item.dropItem)
        .filter(isGroupingFactor)
        .map((gf) => arst_groupingFactorToTableGroup(gf, state.settings.editorProps));
      const newRows = [...newTableGroups, ...state.bodyData.rows];
      const newRowDepth = Math.max(
        ...newRows.map((group) => arst_tableGroupDepth(group, state.settings.editorProps)),
        1,
      );
      const newRowHeaderCell: ArsTableRowHeader<T> = blankRowHeaderCell(
        state.settings.editorProps.blankT,
        state.bodyData.rowHeaders.length > 0
          ? state.bodyData.rowHeaders[0].heights
          : (state.scaffoldModel?.rows.filter((r) => r.type === "header")?.length ?? 1),
      );
      const newRowHeaders: ArsTableRowHeader<T>[] = [
        ...state.bodyData.rowHeaders,
        ...Array.from({ length: newRowDepth - state.bodyData.rowHeaders.length }, () => ({
          ...newRowHeaderCell,
        })),
      ];
      const newBodyData: ArsTableBody<T> = {
        ...state.bodyData,
        rows: newRows,
        rowHeaders: newRowHeaders,
      };
      return {
        ...state,
        bodyData: newBodyData,
        changed: true,
      };
    }
    case ARST_SET_SCAFFOLD_COL_WIDTH: {
      const colCells = getColumnCells(state.calculatedTableRows, action.colId);
      const targetCell = colCells.find(
        (cell) =>
          isArsTableRowHeaderCell(cell, state.settings.editorProps.isT) ||
          isArsTableColumnOperationCell(cell, state.settings.editorProps.isT) ||
          (isArsTableColumnGroupLabel(cell, state.settings.editorProps.isT) &&
            (cell.colSpan ?? 1) === 1),
      );
      if (targetCell && state.scaffoldModel) {
        const targetDataPath = targetCell.dataPath.startsWith("/columns")
          ? targetCell.dataPath + "/dimension"
          : targetCell.dataPath + "/width";
        const newBodyData = mutateByPath(state.bodyData, targetDataPath, action.width);
        const newScaffoldModel = mutateByPath(
          state.scaffoldModel,
          `/cols/${targetCell.columnIndex}/width`,
          action.width,
        );
        return {
          ...state,
          bodyData: newBodyData,
          scaffoldModel: newScaffoldModel,
          changed: true,
        };
      }
      return state;
    }
    case ARST_SET_SCAFFOLD_ROW_HEIGHT: {
      if (state.scaffoldModel) {
        const rowIndex = state.scaffoldModel.rows.findIndex((r) => r.id === action.rowId);
        if (rowIndex === -1) return state;
        const scaffoldRow = state.scaffoldModel.rows[rowIndex];
        // Update scaffoldModel row height
        const newScaffoldModel = mutateByPath(
          state.scaffoldModel,
          `/rows/${rowIndex}/height`,
          action.height,
        );
        // Update body
        /* istanbul ignore else */
        if (scaffoldRow) {
          let newBodyData = { ...state.bodyData };
          // Update rowHeaders heights for header rows
          if (scaffoldRow.type === "header") {
            const newHeights = state.scaffoldModel.rows
              .filter((r) => r.type === "header")
              .map((r) => (r.id === action.rowId ? action.height : r.height));
            newBodyData = mutateByPath(
              state.bodyData,
              `/rowHeaders`,
              state.bodyData.rowHeaders.map((rh) => ({ ...rh, heights: newHeights })),
            );
          }
          // Update rows heights for body rows
          else {
            const targetRow = Object.values(
              state.scaffoldModel.cellData[action.rowId] ??
                // istanbul ignore next
                {},
            );
            const targetCell = targetRow.find(
              (cell) =>
                !("rowSpan" in cell) ||
                (cell.rowSpan ??
                  // istanbul ignore next
                  1) === 1,
            );
            /* istanbul ignore else */
            if (targetCell) {
              // Update body data path height, remove any dataDriven parent paths
              const pathSegments = targetCell.dataPath.split("/");
              let targetDataPath = targetCell.dataPath;
              if (pathSegments[pathSegments.length - 2] === "dataValues") {
                pathSegments.splice(-2, 2).join("/");
                targetDataPath = pathSegments.join("/");
              }
              newBodyData = mutateByPath(newBodyData, targetDataPath + "/dimension", action.height);
            }
          }
          return {
            ...state,
            bodyData: newBodyData,
            scaffoldModel: newScaffoldModel,
            changed: true,
          };
        }
      }
      return state;
    }
    case ARST_SET_SIDEBAR_VIEW:
      return {
        ...state,
        settings: {
          ...state.settings,
          currentSidebar: {
            ...state.settings.currentSidebar,
            view: action.view,
          },
        },
      };
    case ARST_SET_SIDEBAR_FILTER:
      return {
        ...state,
        settings: {
          ...state.settings,
          currentSidebar: {
            ...state.settings.currentSidebar,
            filter: action.filter,
          },
        },
      };
    case ARST_TOGGLE_SIDEBAR:
      return {
        ...state,
        settings: {
          ...state.settings,
          showSidebar: !state.settings.showSidebar,
        },
      };
    case ARST_SET_TLF_VIEW_MODE:
      return {
        ...state,
        settings: {
          ...state.settings,
          tlfViewMode: action.tlfViewMode,
        },
      };
    case ARST_TOGGLE_GROUP_LABEL_VISIBILITY: {
      if (
        action.cellValue.cellType === ArsTableCellType.ColumnGroupCell ||
        action.cellValue.cellType === ArsTableCellType.ColumnGroupLabel ||
        action.cellValue.cellType === ArsTableCellType.RowGroupCell ||
        action.cellValue.cellType === ArsTableCellType.RowGroupLabel
      ) {
        const targetGroupingFactorPath = ["group", "dataDriven"].includes(
          action.cellValue.targetType,
        )
          ? reduceDataPath(action.cellValue.dataPath, 2)
          : action.cellValue.dataPath;
        const targetItem = getFromDataPath(targetGroupingFactorPath, state.bodyData, (obj) =>
          isArsTableSubGroup(obj, state.settings.editorProps.isT),
        );
        const newValue = !targetItem?.hidden;
        const newBodyData = mutateByPath(
          state.bodyData,
          `${targetGroupingFactorPath}/hidden`,
          newValue,
        );
        if (
          action.cellValue.cellType === ArsTableCellType.RowGroupCell ||
          action.cellValue.cellType === ArsTableCellType.RowGroupLabel
        ) {
          // Remove row header if you are taking off a row group label
          if (newValue === true) {
            newBodyData.rowHeaders.splice(
              action.cellValue.columnIndex -
                (action.cellValue.cellType === ArsTableCellType.RowGroupLabel ? 1 : 0),
              1,
            );
          }
          // Add row header if you are putting a row group label back on
          else {
            newBodyData.rowHeaders.splice(action.cellValue.columnIndex, 0, {
              heights: [],
              label: state.settings.editorProps.blankT,
              width: 120,
            });
          }
        }
        const newState = {
          ...state,
          bodyData: newBodyData,
          changed: true,
        };
        return newState;
      }
      break;
    }
    case ARST_UPDATE_SCAFFOLD:
      return {
        ...state,
        calculatedTableRows: action.calculatedTableRows,
        scaffoldModel: buildArsTableScaffoldModel(
          action.calculatedTableRows,
          state.settings.editorProps,
        ),
      };
  }
  return state;
};
